import { useEffect, useState } from 'react';
import { useClient } from 'shared/apollo';
import { getAllSurveys, mapSurveyAPIResponse } from './api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  CustomQuestion,
  CustomSingleQuestion,
  mapQuestionTypeToReport,
} from './utilts';
import {
  getCustomQuestionsByReportId,
  ReportCustomQuestionsAPI,
} from 'npo/pages/ReportBuilder/Components/Review/api';
import { getCustomQuestionId } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import { FeedbackQuestion } from 'npo/pages/NPOFeedback/MakeEdits/api';
import { getGuidanceTextBySection } from 'npo/pages/ReportBuilder/Components/utils';

const getGuidanceText = (surveys: CustomQuestion[]) => {
  const resultWithGuidance: CustomQuestion[] = [];
  const resultWithoutGuidance: CustomQuestion[] = [];

  surveys.forEach(s => {
    const newSurveyWithGuidance: CustomQuestion = { ...s, questions: [] };
    const newSurveyWithoutGuidance: CustomQuestion = { ...s, questions: [] };

    s.questions.forEach(q => {
      if (q.questionType === 'guidance') {
        newSurveyWithGuidance.questions.push(q);
      } else {
        newSurveyWithoutGuidance.questions.push(q);
      }
    });

    resultWithGuidance.push(newSurveyWithGuidance);
    resultWithoutGuidance.push(newSurveyWithoutGuidance);
  });

  return {
    withGuidance: resultWithGuidance,
    withoutGuidance: resultWithoutGuidance,
  };
};

export const useCustomQuestions = ({
  reportId,
  accountId,
  portfolioId,
}: {
  accountId?: string;
  portfolioId?: number;
  reportId?: number | string;
}) => {
  const client = useClient();
  const [getAllSurveysRequest, makeGetAllSurveysRequest] = useGraphQLRequest<
    ReportCustomQuestionsAPI
  >();
  const [guidanceText, setGuidanceText] = useState<CustomQuestion[]>([]);
  const [customQuestions, setCustomQuestions] = useState<CustomQuestion[]>([]);
  const [
    customQuestionsWithoutGuidance,
    setCustomQuestionsWithoutGuidance,
  ] = useState<CustomQuestion[]>([]);

  useEffect(() => {
    if (!client) return;
    if (reportId) {
      makeGetAllSurveysRequest(getCustomQuestionsByReportId(client, reportId));
    } else {
      makeGetAllSurveysRequest(
        getAllSurveys(
          client,
          portfolioId === 0 ? undefined : portfolioId,
          accountId,
        ),
      );
    }
  }, [accountId, portfolioId, reportId, makeGetAllSurveysRequest]);

  useEffect(() => {
    switch (getAllSurveysRequest.type) {
      case 'REQUEST_SUCCESS': {
        const result = mapSurveyAPIResponse(
          getAllSurveysRequest.data.allReportSurvey,
        );
        const guidance = getGuidanceText(result);
        setCustomQuestions(result);
        setGuidanceText(guidance.withGuidance);
        setCustomQuestionsWithoutGuidance(guidance.withoutGuidance);
        break;
      }
      case 'REQUEST_ERROR':
        setCustomQuestions([]);
        break;

      default:
        break;
    }
  }, [getAllSurveysRequest, setCustomQuestions]);

  return {
    guidanceText,
    guidanceTextResponse: getGuidanceTextBySection(guidanceText),
    isLoading:
      getAllSurveysRequest.type === 'REQUEST_INIT' ||
      getAllSurveysRequest.type === 'REQUEST_START',
    customQuestions,
    setCustomQuestions,
    getAllSurveysRequest,
    customQuestionsWithoutGuidance,
  };
};

export interface CustomQuestionsById {
  [id: string]: {
    surveyIndex: number;
    questionIndex: number;
    survey: CustomQuestion;
    question: CustomSingleQuestion;
  };
}

export const customQuestionsById = (
  customQuestions: CustomQuestion[],
): CustomQuestionsById => {
  const result: CustomQuestionsById = {};
  for (let i = 0; i < customQuestions.length; i++) {
    const c = customQuestions[i];
    for (let j = 0; j < c.questions.length; j++) {
      const q = c.questions[j];
      result[getCustomQuestionId(q.dbId)] = {
        survey: c,
        question: q,
        surveyIndex: i,
        questionIndex: j,
      };
    }
  }
  return result;
};

export const customQuestionToFeedbackQuestion = (
  c: CustomSingleQuestion | undefined,
): FeedbackQuestion | undefined => {
  if (!c) return undefined;
  const answers: FeedbackQuestion['answers'] =
    c.answer.length === 1
      ? [
          {
            date1: null,
            date2: null,
            text: c.answer[0].value,
            dbId: Number(c.answer[0].dbId),
            questionText: c.questionText,
          },
        ]
      : [];
  return {
    answers: answers,
    dbId: Number(c.dbId),
    enumResponses: [],
    text: c.questionText,
    questionType: mapQuestionTypeToReport(c),
  };
};
